import useGlobalFilters from '../../../../../hooks/useGlobalFilters';
import { useGetProvidersQuery } from '../../../../../services/providersApi';
import useRegion from '../../../../../hooks/useRegion';
import { useEffect, useMemo, useState } from 'react';
import {
    useAssignPreventorMutation,
    useGetPreventorsQuery,
    useReassignPreventorMutation
} from '../../../../../services/visitasApi';
import { Visita } from '../../../consts/visitasTypes';
import { enqueueSnackbar } from 'notistack';
import { handleErrors } from '../../../../../utils/common';
import useAlerts from './useAlerts';
import { PreventorVisitaApi } from '../../../consts/types';
import { soloPreventoresActivos } from '../../../utils/preventoresActivos';
import { useDispatch } from 'react-redux';
import { hideAlert } from '../../../../../features/slices/applicationSlice';
import { selectCurrentUser } from '../../../../../features/auth/authSlice';
import { useAppSelector } from '../../../../../hooks/reduxHooks';
import { useRoles } from '../../../../../hooks/useRoles';

const useAssignPreventor = ({
    isMassiveAssignation,
    singleSelectedVisit,
    closeDrawer,
    onSuccess,
    selectedVisitsIds = [],
    isReassignation = false,
    clearVisitsSelection
}: {
    isMassiveAssignation: boolean;
    singleSelectedVisit: Visita | null;
    closeDrawer: () => void;
    onSuccess?: () => void;
    selectedVisitsIds: number[];
    isReassignation?: boolean;
    clearVisitsSelection: () => void;
}) => {
    const dispatch = useDispatch();
    const currentUser = useAppSelector(selectCurrentUser);
    const { isProvider } = useRoles();
    const searchPreventorFilters = useGlobalFilters();
    const assignPreventorRegion = useRegion();

    const [selectedPreventor, setSelectedPreventor] = useState<PreventorVisitaApi>();
    const [observacion, setObservacion] = useState<string>('');
    const [reassignationMotive, setReassignationMotive] = useState<string>('');

    const [assignPreventor, { isLoading: isAssigningPreventor }] = useAssignPreventorMutation();

    const [reassignPreventor, { isLoading: isReassigningPreventor }] =
        useReassignPreventorMutation();

    const { provider, idProvincia, idLocalidad } = searchPreventorFilters.extraFilters || {};
    const loggedProvider = useMemo(() => {
        const userProvider =
            (currentUser as any)?.proveedor ||
            (currentUser as any)?.preventor?.proveedor ||
            ((currentUser as any)?.idProveedor
                ? {
                      id: (currentUser as any).idProveedor,
                      razonSocial: (currentUser as any)?.razonSocialProveedor || ''
                  }
                : null);

        return userProvider?.id ? userProvider : null;
    }, [currentUser]);
    const effectiveProvider = isProvider ? loggedProvider : provider;

    const providersQueryFilters = {
        ...(idProvincia && { idProvincia }),
        ...(idLocalidad && { idLocalidad }),
        activo: true,
        take: 999999,
        skip: 0
    };

    const { data: providers, isFetching: isFetchingProviders } = useGetProvidersQuery(
        providersQueryFilters,
        { skip: Boolean(isProvider) }
    );

    const preventorsFilters = {
        ...(effectiveProvider?.id && { idProveedor: effectiveProvider.id }),
        ...(idProvincia && { idProvincia }),
        ...(idLocalidad && { idLocalidad })
    };

    const isSearchingProvider = Boolean(idProvincia || idLocalidad);
    const shouldFetchPreventors = isProvider
        ? Boolean(effectiveProvider?.id)
        : !isSearchingProvider || Boolean(effectiveProvider?.id);

    const { data: preventors, isFetching: isFetchingPreventors } = useGetPreventorsQuery(
        preventorsFilters,
        {
            skip: !shouldFetchPreventors
        }
    );

    const {
        showConfirmPreventorAssignationAlert,
        showConfirmPreventorRessignationAlert,
        showDifferentProviderAlert
    } = useAlerts();

    const handleSelectPreventor = (value: PreventorVisitaApi) => {
        setSelectedPreventor(value);
    };

    const handleClearFilters = () => {
        searchPreventorFilters.clearFilters();
        assignPreventorRegion.resetRegion();
        setSelectedPreventor(undefined);
    };

    const handleChangeObservation = (event: React.ChangeEvent<HTMLInputElement>) => {
        setObservacion(event.target.value);
    };

    const handleChangeReassignationMotive = (event: React.ChangeEvent<HTMLInputElement>) => {
        setReassignationMotive(event.target.value);
    };

    const handleSubmit = () => {
        if (!selectedPreventor?.idUsuario) {
            enqueueSnackbar('Debe seleccionar un preventor', { variant: 'warning' });
            return;
        }

        if (isReassignation) {
            if (
                !reassignationMotive ||
                reassignationMotive.length < 10 ||
                reassignationMotive.length > 300
            ) {
                enqueueSnackbar('El motivo de reasignación debe tener entre 10 y 300 caracteres', {
                    variant: 'warning'
                });
                return;
            }
        }

        const isDifferentProvider = singleSelectedVisit
            ? effectiveProvider?.id !== singleSelectedVisit?.preventor?.proveedor?.id
            : false;

        if (isReassignation) {
            if (isDifferentProvider) {
                showDifferentProviderAlert({
                    confirmAction: handleReassignPreventor,
                    cancelAction: () => dispatch(hideAlert())
                });
                return;
            }

            showConfirmPreventorRessignationAlert({
                confirmAction: handleReassignPreventor,
                cancelAction: () => dispatch(hideAlert()),
                currentVisitsNumber: selectedPreventor?.visitas?.length || 0
            });
        } else {
            showConfirmPreventorAssignationAlert({
                confirmAction: handleAssignPreventor,
                cancelAction: () => dispatch(hideAlert()),
                currentVisitsNumber: selectedPreventor?.visitas?.length || 0
            });
        }
    };

    const handleAssignPreventor = async () => {
        if (!selectedPreventor?.idUsuario) return;

        if (!selectedPreventor?.activo) {
            enqueueSnackbar('El preventor seleccionado está inactivo y no puede ser asignado.', {
                variant: 'warning'
            });
            return;
        }

        try {
            const adaptedBody = {
                idsVisita: isMassiveAssignation
                    ? selectedVisitsIds
                    : [singleSelectedVisit?.id as number],
                observacion: observacion || ''
            };

            await assignPreventor({
                preventorID: selectedPreventor.idUsuario,
                body: adaptedBody
            }).unwrap();

            enqueueSnackbar('Preventor asignado correctamente', {
                variant: 'success'
            });

            clearVisitsSelection();
            onSuccess?.();
            closeDrawer();
        } catch (error: any) {
            error?.data?.message
                ? handleErrors(error)
                : enqueueSnackbar('Error al asignar preventor', {
                      variant: 'error'
                  });
        }
    };

    const handleReassignPreventor = async () => {
        if (!selectedPreventor?.idUsuario) return;

        try {
            const adaptedBody = {
                idsVisita: isMassiveAssignation
                    ? selectedVisitsIds
                    : [singleSelectedVisit?.id as number],
                observacion: observacion || '',
                motivo: reassignationMotive
            };

            await reassignPreventor({
                preventorID: selectedPreventor.idUsuario,
                body: adaptedBody
            }).unwrap();

            enqueueSnackbar('Preventor reasignado correctamente', {
                variant: 'success'
            });

            onSuccess?.();
            closeDrawer();
        } catch (error: any) {
            error?.data?.message
                ? handleErrors(error)
                : enqueueSnackbar('Error al reasignar preventor', {
                      variant: 'error'
                  });
        }
    };

    useEffect(() => {
        setSelectedPreventor(undefined);
    }, [effectiveProvider?.id, idProvincia, idLocalidad]);

    return {
        searchPreventorFilters,
        providersOptions:
            isProvider && loggedProvider ? [loggedProvider] : providers?.data?.data || [],
        selectedProvider: effectiveProvider || null,
        isProviderFilterLocked: Boolean(isProvider),
        preventorsOptions: shouldFetchPreventors
            ? soloPreventoresActivos<PreventorVisitaApi>(preventors?.data)
            : [],
        isFetchingProviders,
        isFetchingPreventors,
        assignPreventorRegion,
        selectedPreventor,
        handleSelectPreventor,
        observacion,
        handleChangeObservation,
        reassignationMotive,
        handleChangeReassignationMotive,
        handleSubmit,
        isAssigningPreventor,
        isReassigningPreventor,
        handleClearFilters
    };
};

export default useAssignPreventor;
